import { TranslatePipe } from 'app/pipes/translate.pipe';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MdDialog, MdDialogRef } from '@angular/material';
import { PinComponent } from './pin/pin.component';
import { Subscription } from 'rxjs';
import { BlacklistPIN } from 'app/model/constant';
import { ActivatedRoute, Router } from '@angular/router';
import { PinService } from 'app/services/pin/pin.service';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { DialogAlertComponent } from '../dialog-alert/dialog-alert.component';
import { DialogErrorComponent } from '../dialog-error/dialog-error.component';
import { OTP_STATE } from 'app/components/verify-otp/otp-state';
import { PIN_STATE } from './pin-state';
import { VerifyOtpComponent } from 'app/components/verify-otp/verify-otp.component';
import { UserService } from 'app/services/user/user.service';
import { OTPPurpose, VerificationMethodModel } from 'app/model/otp/otp.model';
import { VerifyPINResponse } from 'app/model/pin/pin.model';

@Component({
  selector: 'act-dialog-pin',
  templateUrl: './dialog-pin.component.html',
  styleUrls: ['./dialog-pin.component.scss']
})
export class DialogPinComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('pinChild') pinChild: PinComponent;
  @ViewChild('otpVerifyChild') otpVerifyChild: VerifyOtpComponent;

  public backButtonMode?: 'back' | 'cancel' = 'cancel';
  public elementId = 'dialog-pin';
  public sendingRequestOtp = false;
  public isLoading = false;
  public errorMessage: string;
  public requiredSetPin: boolean = false;
  public lang: string;
  public PIN_STATE = PIN_STATE;
  public OTP_PURPOSE = OTPPurpose;

  public otpMethods: VerificationMethodModel[] = []
  public otpToken: string;

  private pinLocked = false;
  private accountLocked = false;
  private verifyPinSubscription: Subscription;
  private langSubscription: Subscription;
  private inputPin = '';

  @Input() state: PIN_STATE = PIN_STATE.SET_PIN;

  constructor(
    public dialogRef: MdDialogRef<DialogPinComponent>,
    private translatePipe: TranslatePipe,
    private route: ActivatedRoute,
    private pinService: PinService,
    private appConfigService: AppConfigService,
    private userService: UserService,
    private dialog: MdDialog,
    private cd: ChangeDetectorRef,
    private router: Router,
  ) { }

  ngOnDestroy(): void {
    this.verifyPinSubscription && this.verifyPinSubscription.unsubscribe();
  }

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('set_pin')) {
      this.backButtonMode = undefined;
      this.requiredSetPin = true;
    }
    this.subscribeLanguageChanges()
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  subscribeLanguageChanges(): void {
    this.lang = this.appConfigService.getLanguage();
    this.langSubscription && this.langSubscription.unsubscribe();
    this.langSubscription = this.appConfigService.getLanguageObs().subscribe(lang => {
      this.lang = lang;
    });
  }

  onSubmit() {
    if (this.state == PIN_STATE.CHECKOUT) {
      this.verifyPIN();
    } else if (this.state == PIN_STATE.SET_PIN) {
      this.validatePIN();
    } else if (this.state == PIN_STATE.CONFIRM_SET_PIN) {
      this.validateConfirmPIN();
      this.setupPIN();
    } else if (this.state == PIN_STATE.METHODS) {
      this.onClickCancel()
    } else if (this.state == PIN_STATE.OTP) {
      this.isLoading = true;
      this.otpVerifyChild.onSubmit()
    }
  }

  validatePIN() {
    this.errorMessage = '';
    if (BlacklistPIN.includes(this.pinChild.rawPin)) {
      setTimeout(() => {
        this.errorMessage = this.translatePipe.transform('pin-too-weak');
      }, 0);
      return;
    }
    this.inputPin = this.pinChild.rawPin;
    this.state = PIN_STATE.CONFIRM_SET_PIN
    this.backButtonMode = 'back';
  }

  validateConfirmPIN() {
    this.errorMessage = '';
    if (this.inputPin != this.pinChild.rawPin) {
      setTimeout(() => {
        this.errorMessage = this.translatePipe.transform('set-pin-not-matched');
      }, 0);
      throw new Error(this.errorMessage)
    }
  }

  verifyPIN(): void {
    if (this.pinInputForm.valid && !this.pinLocked) {
      this.isLoading = true;
      const rawPin = this.pinChild.rawPin;
      this.verifyPinSubscription && this.verifyPinSubscription.unsubscribe();
      this.verifyPinSubscription = this.pinService.verifyPIN(rawPin)
        .subscribe(res => {
          if (res && res.verifiedToken) {
            this.dialogRef.close({ verifiedToken: res.verifiedToken })
          }
          this.isLoading = false;
        },
        (error) => {
          if (error.status === 403) {
            this.errorMessage = this.translatePipe.transform('pin-locked');
            this.pinLocked = true;
          } else {
            this.errorMessage = this.translatePipe.transform('pin-code-incorrect');
            if (error && error.json() && error.json().errors && error.json().errors.length > 0) {
              this.handleInvalidPIN(error.json().errors[0].message);
            }
          }
          this.isLoading = false;
        });
    }
  }

  handleInvalidPIN(message: string): void {
    const match = message.match(/\{(\d+)\}/);
    if (match && match[1]) {
      const remaining = +match[1];
      if (remaining <= 3) {
        this.errorMessage = this.translatePipe.transform('pin-remaining-attempts').replace('{x}', remaining);
      }
    }
  }

  resetError() {
    this.errorMessage = undefined;
    this.pinLocked = false;
  }

  onClickBack() {
    if (this.accountLocked) {
      this.userService.logout();
      return;
    }
    if (this.state == PIN_STATE.CONFIRM_SET_PIN) {
      this.state = PIN_STATE.SET_PIN;
      if (this.requiredSetPin) {
        this.backButtonMode = undefined;
      } else {
        this.backButtonMode = 'cancel';
      }
    } else if (this.state == PIN_STATE.OTP) {
      this.state = PIN_STATE.METHODS;
      this.backButtonMode = undefined;
    }
  }

  onClickCancel() {
    if (this.accountLocked) {
      this.dialogRef.afterClosed().take(1).subscribe(() => {
        this.userService.logout();
      });
    }
    this.dialogRef.close({close: true});
  }

  get pinInputForm() {
    if (this.pinChild) {
      return this.pinChild.pinInputForm;
    }
    return undefined;
  }

  get otpInputForm() {
    if (this.otpVerifyChild) {
      return this.otpVerifyChild.otpInputForm
    }
    return undefined;
  }

  setupPIN(verifiedToken?: string) {
    this.isLoading = true;
    this.pinService.getPublicKey().flatMap((pk) => {
      return this.pinService.setupPIN(this.inputPin, pk.token, pk.key, verifiedToken);
    }).subscribe((res) => {
      if (res.verification) {
        this.needVerifyOtp(res);
      } else {
        this.dialogRef.afterClosed().take(1).subscribe(() => {
          this.showDialogSetupPinSuccess();
        })
        this.dialogRef.close()
      }
    }, error => {
      this.dialogRef.afterClosed().take(1).subscribe(() => {
        this.showDialogError(this.translatePipe.transform('system-error'))
      })
      this.dialogRef.close()
    });
  }

  needVerifyOtp(res) {
    this.otpToken = res.verification.token;
    this.otpMethods = res.verification.methods;
    this.state = PIN_STATE.METHODS;
    this.backButtonMode = undefined;
    this.isLoading = false;
    this.cd.detectChanges()
  }

  showDialogSetupPinSuccess() {
    const successPopupRef = this.dialog.open(DialogAlertComponent)
    successPopupRef.afterClosed().take(1).subscribe(() => {
      const queryParam = new URLSearchParams(window.location.search);
      queryParam.delete('set_pin')
      this.router.navigateByUrl(`/setting?${queryParam.toString()}`, {replaceUrl: true})
    })
    successPopupRef.componentInstance.imageSrc = '/assets/images/icons/icon-setup-pin-success.png'
    successPopupRef.componentInstance.alertHeader = this.translatePipe.transform('setup-pin-success');
    successPopupRef.componentInstance.autoClose = 10;
  }

  showDialogError(message: string, subText?: string) {
    const dialogRef: MdDialogRef<DialogErrorComponent> = this.dialog.open(DialogErrorComponent);
    dialogRef.componentInstance.text = message;
    if (subText) {
      dialogRef.componentInstance.subText = subText;
    }
    return dialogRef;
  }

  onVerifiedOtp(event: VerifyPINResponse) {
    this.setupPIN(event.verifiedToken)
  }

  onOtpStateChange(event: OTP_STATE) {
    this.state = event;
    if (event == OTP_STATE.METHODS) {
      this.backButtonMode = undefined;
    } else if (event == OTP_STATE.OTP) {
      this.backButtonMode = 'back';
    } else if (event == OTP_STATE.SESSION_EXPIRE) {
      this.backButtonMode = 'cancel';
    }
  }

  onAccountLocked() {
    this.accountLocked = true;
  }

}



// WEBPACK FOOTER //
// ./src/app/components/modal/dialog-pin/dialog-pin.component.ts